import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Trash2, LogOut, RefreshCw } from "lucide-react";
import {
  fetchInquiries,
  fetchStats,
  updateInquiryStatus,
  deleteInquiry,
  verifyAdmin,
} from "../lib/api";

const STORAGE_KEY = "ervion.admin.token";

const StatusPill = ({ status }) => {
  const map = {
    new: "bg-[#C5A880]/20 text-[#0A192F] border-[#C5A880]",
    reviewed: "bg-slate-100 text-slate-700 border-slate-300",
    contacted: "bg-[#0A192F] text-white border-[#0A192F]",
  };
  return (
    <span className={`inline-block px-3 py-1 text-[10px] uppercase tracking-[0.22em] border ${map[status] || map.new}`}>
      {status}
    </span>
  );
};

export default function Admin() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      verifyAdmin(stored)
        .then(() => {
          setToken(stored);
          setAuthed(true);
        })
        .catch(() => localStorage.removeItem(STORAGE_KEY));
    }
  }, []);

  useEffect(() => {
    if (authed) refresh();
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await verifyAdmin(token);
      localStorage.setItem(STORAGE_KEY, token);
      setAuthed(true);
      toast.success("Signed in");
    } catch {
      toast.error("Invalid token");
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setToken("");
    setInquiries([]);
    setStats(null);
    setSelected(null);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([fetchInquiries(token), fetchStats(token)]);
      setInquiries(list);
      setStats(s);
    } catch (e) {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      const updated = await updateInquiryStatus(token, id, status);
      setInquiries((prev) => prev.map((i) => (i.id === id ? updated : i)));
      if (selected?.id === id) setSelected(updated);
      toast.success("Status updated");
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await deleteInquiry(token, id);
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Inquiry deleted");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!authed) {
    return (
      <div data-testid="admin-login-page" className="min-h-[80vh] flex items-center justify-center px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-[#E2E8F0] bg-white p-10"
        >
          <div className="font-sans text-[11px] tracking-[0.28em] uppercase text-[#C5A880]">
            Ervion · Admin Access
          </div>
          <h1 className="mt-4 font-serif text-4xl text-[#0A192F] tracking-tight">Sign in.</h1>
          <p className="mt-3 font-sans text-sm text-slate-600">
            Enter your admin token to view submitted inquiries.
          </p>
          <div className="mt-8 space-y-2">
            <Label htmlFor="token" className="text-xs uppercase tracking-[0.22em] text-slate-600">
              Admin Token
            </Label>
            <Input
              id="token"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              data-testid="admin-token-input"
              className="rounded-none border-0 border-b border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#0A192F] px-0 h-11 font-serif text-lg"
              placeholder="Enter token"
              required
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="mt-10 w-full bg-[#0A192F] text-white py-4 font-sans text-[12px] tracking-[0.22em] uppercase hover:bg-[#1E293B] transition-all"
          >
            Enter Dashboard
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="font-sans text-[11px] tracking-[0.28em] uppercase text-[#C5A880]">
            Ervion · Admin
          </div>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl text-[#0A192F] tracking-tight">
            Inquiries
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refresh}
            data-testid="admin-refresh-btn"
            className="inline-flex items-center gap-2 border border-[#0A192F] text-[#0A192F] px-4 py-2 font-sans text-[11px] tracking-[0.22em] uppercase hover:bg-slate-50"
          >
            <RefreshCw size={14} strokeWidth={1.5} /> Refresh
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-4 py-2 font-sans text-[11px] tracking-[0.22em] uppercase hover:bg-[#1E293B]"
          >
            <LogOut size={14} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </div>

      {stats && (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-5 border-t border-l border-[#E2E8F0]">
          {[
            { label: "Total", value: stats.total_inquiries, testid: "stat-total" },
            { label: "New", value: stats.new, testid: "stat-new" },
            { label: "Reviewed", value: stats.reviewed, testid: "stat-reviewed" },
            { label: "Contacted", value: stats.contacted, testid: "stat-contacted" },
            { label: "Newsletter", value: stats.newsletter_subscribers, testid: "stat-newsletter" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="border-r border-b border-[#E2E8F0] p-6">
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500">{s.label}</div>
              <div className="mt-2 font-serif text-4xl text-[#0A192F] tracking-tight">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-14 border border-[#E2E8F0]">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[#E2E8F0]">
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Date</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Name</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Email</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Company</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Service</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-slate-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-slate-500 font-sans text-sm">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && inquiries.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-16 text-slate-500 font-sans" data-testid="admin-empty">
                  No inquiries yet.
                </TableCell>
              </TableRow>
            )}
            {inquiries.map((row) => (
              <TableRow
                key={row.id}
                data-testid={`inquiry-row-${row.id}`}
                className="cursor-pointer hover:bg-[#F8F9FA]"
                onClick={() => setSelected(row)}
              >
                <TableCell className="font-sans text-xs text-slate-600">
                  {new Date(row.created_at).toLocaleString()}
                </TableCell>
                <TableCell className="font-serif text-base text-[#0A192F]">{row.name}</TableCell>
                <TableCell className="font-sans text-sm text-slate-700">{row.email}</TableCell>
                <TableCell className="font-sans text-sm text-slate-700">{row.company || "—"}</TableCell>
                <TableCell className="font-sans text-sm text-slate-700">{row.service || "—"}</TableCell>
                <TableCell><StatusPill status={row.status} /></TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex items-center gap-2">
                    <Select value={row.status} onValueChange={(v) => changeStatus(row.id, v)}>
                      <SelectTrigger data-testid={`status-select-${row.id}`} className="rounded-none h-8 w-32 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="reviewed">Reviewed</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      onClick={() => remove(row.id)}
                      data-testid={`delete-btn-${row.id}`}
                      className="p-2 text-slate-400 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {selected && (
        <div
          data-testid="inquiry-detail"
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="max-w-2xl w-full bg-white border border-[#E2E8F0] p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div className="font-sans text-[11px] tracking-[0.28em] uppercase text-[#C5A880]">Inquiry</div>
              <button onClick={() => setSelected(null)} data-testid="close-detail-btn" className="text-slate-500 hover:text-[#0A192F] text-sm uppercase tracking-[0.2em]">
                Close
              </button>
            </div>
            <h2 className="mt-4 font-serif text-4xl text-[#0A192F] tracking-tight">{selected.name}</h2>
            <div className="mt-2 font-sans text-sm text-slate-600">
              {selected.email} {selected.phone && `· ${selected.phone}`}
            </div>
            <div className="mt-2 font-sans text-xs text-slate-500 uppercase tracking-[0.18em]">
              {selected.company || "No company"} · {selected.service || "No service"}
            </div>
            <div className="mt-8 border-t border-[#E2E8F0] pt-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-3">Message</div>
              <p className="font-sans text-base text-slate-800 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
            </div>
            <div className="mt-8 flex items-center justify-between">
              <StatusPill status={selected.status} />
              <div className="text-xs text-slate-500 font-sans">
                {new Date(selected.created_at).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
